import {useCallback, useEffect, useState} from "react";
import {useUser} from "UserProvider";
import {
	ConsentState,
	denyAllConsent,
	getConsent,
	grantAllConsent,
	saveConsentChoices,
	subscribeConsent,
} from "consent/consentStore";

/**
 * React binding for the cookie-consent store (ui#72; advertising purpose added in ui#256; the optional
 * email-offers choice in ui#2331).
 *
 * Exposes the live {@link ConsentState} plus the three decisions the banner and settings dialog record:
 *   - `acceptAll()`  — grant analytics + advertising + the email-offers choice, mark decided.
 *   - `rejectAll()`  — deny all three, mark decided.
 *   - `savePreferences({analytics, marketing, email})` — the settings dialog's "Save"; the email
 *     boolean is the toggle position, and the store records exactly it (owner decision, 2026-09-09:
 *     "Saved OFF stops emails" — an off box is a denial, touched or not).
 *
 * It deliberately does NOT start or stop any tag. Tagging follows the STORE, wired once by
 * `analytics/useAnalyticsConsent` — a hook that unmounts (this one lives in the banner, which disappears
 * the moment consent is decided) must never be the thing responsible for applying a withdrawal.
 *
 * SSG-safe: the initial state is the store's safe default during pre-render; a mount effect re-reads the
 * persisted value (so a returning visitor's choice is reflected after hydration) and subscribes.
 */
export interface UseConsentResult {
	consent: ConsentState;
	acceptAll: () => void;
	rejectAll: () => void;
	savePreferences: (choices: {analytics: boolean; marketing: boolean; email: boolean}) => void;
}

export function useConsent(): UseConsentResult {
	const [consent, setState] = useState<ConsentState>(() => getConsent());
	const [user] = useUser();
	const accountId = user.isLoggedIn ? user.id : null;

	useEffect(() => {
		// Re-read after hydration (pre-render used the default) and keep in sync with store writes.
		setState(getConsent());
		return subscribeConsent(setState);
	}, []);

	const acceptAll = useCallback(() => {
		grantAllConsent(accountId);
	}, [accountId]);

	const rejectAll = useCallback(() => {
		denyAllConsent(accountId);
	}, [accountId]);

	const savePreferences = useCallback((choices: {analytics: boolean; marketing: boolean; email: boolean}) => {
		saveConsentChoices(choices, accountId);
	}, [accountId]);

	return {consent, acceptAll, rejectAll, savePreferences};
}
